import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardContent, Typography, Box, ToggleButtonGroup, ToggleButton, IconButton, Menu, MenuItem } from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import GridViewIcon from '@mui/icons-material/GridView';
import DownloadIcon from '@mui/icons-material/Download';
import DelightFeatures from './DelightFeatures';
import NegativeIssues from './NegativeIssues';
import captureScreenshot from 'utils/screenshotUtils';
import { motion } from 'framer-motion';

/**
 * Component that combines DelightFeatures and NegativeIssues into a 2x2 matrix
 * and provides a toggle to switch between views
 */
const FeatureIssueMatrix = () => {
  const [view, setView] = useState('matrix');
  const [showQuotes, setShowQuotes] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const contentRef = useRef(null);

  // Get the data from redux
  const delight_features = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.delight_features);
  const negative_issues = useSelector((state) => state.reviewsReducer.v2Data.review_analysis_results.negative_issues);

  // If neither data is available, don't render anything
  if (!delight_features && !negative_issues) {
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const downloadAsPng = () => {
    captureScreenshot({
      contentRef,
      filename: 'features-issues-matrix',
      afterCapture: handleClose
    });
  };

  // Process data for matrix view
  const processMatrixData = () => {
    const result = {
      'high-positive': [],
      'high-negative': [],
      'low-positive': [],
      'low-negative': []
    };

    // Combine all features and issues for unified frequency threshold
    const allItems = [];

    if (delight_features) {
      // Format delight features
      const features = Object.entries(delight_features).map(([name, data]) => ({
        name,
        ...data,
        type: 'positive'
      }));

      allItems.push(...features);
    }

    if (negative_issues) {
      // Format negative issues
      const issues = Object.entries(negative_issues).map(([name, data]) => ({
        name,
        ...data,
        type: 'negative'
      }));

      allItems.push(...issues);
    }

    // Find median frequency as the threshold from all items combined
    const frequencies = allItems.map((item) => item.frequency);
    const medianFrequency = frequencies.length > 0 ? frequencies.sort((a, b) => a - b)[Math.floor(frequencies.length / 2)] : 0;

    // Sort into high and low frequency
    allItems.forEach((item) => {
      if (item.frequency >= medianFrequency) {
        if (item.type === 'positive') {
          result['high-positive'].push(item);
        } else {
          result['high-negative'].push(item);
        }
      } else {
        if (item.type === 'positive') {
          result['low-positive'].push(item);
        } else {
          result['low-negative'].push(item);
        }
      }
    });

    // Sort each category by frequency (descending)
    Object.keys(result).forEach((key) => {
      result[key].sort((a, b) => b.frequency - a.frequency);
    });

    return result;
  };

  // Render item for matrix view
  const renderFeatureItem = (item) => {
    // Define color scheme based on item type (positive/negative)
    const borderColor = item.type === 'positive' ? 'success.main' : 'error.main';

    return (
      <Box
        key={item.name}
        sx={{
          mb: 1.5,
          ml: 1,
          pl: 2,
          borderLeft: '3px solid',
          borderColor: borderColor,
          py: 0.5
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {item.name} ({item.frequency} mentions)
        </Typography>

        {/* Quote - conditionally rendered based on showQuotes state */}
        {showQuotes && item.type === 'positive' && item.quotes && item.quotes[0] && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.5
            }}
          >
            &quot;{item.quotes[0]}&quot;
          </Typography>
        )}

        {showQuotes && item.type === 'negative' && item.representative_quotes && item.representative_quotes[0] && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.5
            }}
          >
            &quot;{item.representative_quotes[0]}&quot;
          </Typography>
        )}
      </Box>
    );
  };

  // Render the split view (original components side by side)
  const renderSplitView = () => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
      {delight_features && (
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 100,
            damping: 15,
            duration: 0.6
          }}
          whileHover={{
            y: -5,
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            transition: { duration: 0.3 }
          }}
          style={{ flex: 1 }}
        >
          <Card
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
              },
              height: '100%'
            }}
          >
            <DelightFeatures showQuotes={showQuotes} />
          </Card>
        </motion.div>
      )}
      {negative_issues && (
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 100,
            damping: 15,
            duration: 0.6
          }}
          whileHover={{
            y: -5,
            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
            transition: { duration: 0.3 }
          }}
          style={{ flex: 1 }}
        >
          <Card
            sx={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
              },
              height: '100%'
            }}
          >
            <NegativeIssues showQuotes={showQuotes} />
          </Card>
        </motion.div>
      )}
    </Box>
  );

  // Render the matrix view (2x2 grid)
  const renderMatrixView = () => {
    const matrixData = processMatrixData();

    return (
      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Features & Issues Matrix
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Overview of appreciated features and critical issues by frequency
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 3,
              mt: 3
            }}
          >
            {/* Quadrant 1: High Frequency Positive */}
            <Card
              className="feature-quadrant-card"
              sx={{
                p: 2,
                height: '100%',
                background: 'linear-gradient(135deg, rgba(227, 252, 236, 0.6) 0%, rgba(227, 252, 236, 0.01) 100%)'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                High Frequency • Appreciated Features
              </Typography>
              <Box sx={{ flex: 1 }}>
                {matrixData['high-positive'].length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No high frequency appreciated features found
                  </Typography>
                ) : (
                  matrixData['high-positive'].map(renderFeatureItem)
                )}
              </Box>
            </Card>

            {/* Quadrant 2: High Frequency Negative */}
            <Card
              className="feature-quadrant-card"
              sx={{
                p: 2,
                height: '100%',
                background: 'linear-gradient(135deg, rgba(253, 236, 234, 0.6) 0%, rgba(253, 236, 234, 0.01) 100%)'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                High Frequency • Critical Issues
              </Typography>
              <Box sx={{ flex: 1 }}>
                {matrixData['high-negative'].length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No high frequency critical issues found
                  </Typography>
                ) : (
                  matrixData['high-negative'].map(renderFeatureItem)
                )}
              </Box>
            </Card>

            {/* Quadrant 3: Low Frequency Positive */}
            <Card
              className="feature-quadrant-card"
              sx={{
                p: 2,
                height: '100%',
                background: 'linear-gradient(135deg, rgba(227, 252, 236, 0.4) 0%, rgba(227, 252, 236, 0.01) 100%)'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Low Frequency • Appreciated Features
              </Typography>
              <Box sx={{ flex: 1 }}>
                {matrixData['low-positive'].length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No low frequency appreciated features found
                  </Typography>
                ) : (
                  matrixData['low-positive'].map(renderFeatureItem)
                )}
              </Box>
            </Card>

            {/* Quadrant 4: Low Frequency Negative */}
            <Card
              className="feature-quadrant-card"
              sx={{
                p: 2,
                height: '100%',
                background: 'linear-gradient(135deg, rgba(253, 236, 234, 0.4) 0%, rgba(253, 236, 234, 0.01) 100%)'
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Low Frequency • Critical Issues
              </Typography>
              <Box sx={{ flex: 1 }}>
                {matrixData['low-negative'].length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No low frequency critical issues found
                  </Typography>
                ) : (
                  matrixData['low-negative'].map(renderFeatureItem)
                )}
              </Box>
            </Card>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box ref={contentRef}>
      {/* View controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 2,
          gap: 1
        }}
        className="screenshot-controls"
      >
        {/* Hide/Show Quotes Toggle */}
        <ToggleButton
          value="quotes"
          selected={showQuotes}
          onChange={() => setShowQuotes(!showQuotes)}
          size="small"
          sx={{
            height: 28,
            px: 1,
            fontSize: '0.75rem'
          }}
        >
          {showQuotes ? 'Hide Quotes' : 'Show Quotes'}
        </ToggleButton>

        <IconButton onClick={handleClick} size="small" sx={{ height: 28 }} aria-label="download options">
          <DownloadIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              border: 1,
              borderColor: 'secondary.light',
              borderRadius: 1
            }
          }}
        >
          <MenuItem onClick={downloadAsPng}>Download as PNG</MenuItem>
        </Menu>

        <ToggleButtonGroup value={view} exclusive onChange={handleViewChange} size="small">
          <ToggleButton value="split" aria-label="split view">
            <TableChartOutlinedIcon />
          </ToggleButton>
          <ToggleButton value="matrix" aria-label="matrix view">
            <GridViewIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Main content */}
      {view === 'split' ? renderSplitView() : renderMatrixView()}
    </Box>
  );
};

export default FeatureIssueMatrix;
